import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import TooltipPopover from './tooltip-popover';
import VariablePricingTooltip from '../elements-forms/variable-pricing-tooltip';
import IntlProviderHOC from '../app-main/containers/IntlProviderHOC';
import SharedIntlProviderHOC from '../shared-components/hocs/sharedIntlProviderHOC';
import { guestVatSupportLink, hostVatSupportLink } from '../app-payments/data/supportLinks';

export const getTooltipData = () => {
  const zendeskLocale = SharedIntlProviderHOC.getZendeskLocale();
  return {
    SERVICE_FEE: {
      content:
        <div>
          This fee allows Peerspace to promote your space, provide insurance coverage, and include support for your bookings. <a href={`//support.peerspace.com/hc/${zendeskLocale}/articles/201721487-What-is-the-Peerspace-service-fee`} target="_blank">Learn More</a>
        </div>,
      settings: {
        placement: 'top'
      },
      className: 'inline-block'
    },
    PRICE_UPDATE_EIGHT_HOUR_DISCOUNT_LOSS: {
      content:
        <div>
          The price has been updated and the discount is no longer valid.
        </div>,
      settings: {
        placement: 'top'
      },
      className: 'inline-block'
    },
    HOST_UPDATE_EIGHT_HOUR_DISCOUNT_LOSS: {
      content:
        <div>
          Any discounts should be included in your subtotal above.
        </div>,
      settings: {
        placement: 'top'
      },
      className: 'inline-block'
    },
    EIGHT_HOUR_DISCOUNT_LOSS: {
      content:
        <div>
          This discount doesn’t apply anymore, since your new booking is less than 8 hours.
        </div>,
      settings: {
        placement: 'top'
      },
      className: 'inline-block'
    },
    HOST_PAYOUT (content) {
      const toolData = {
        content,
        settings: {
          placement: 'top'
        },
        className: 'inline-block'
      };
      return toolData;
    },
    HOST_PAYOUT_UPDATE (content) {
      const toolData = {
        content,
        settings: {
          placement: 'bottom'
        },
        className: 'inline-block'
      };
      return toolData;
    },
    HOST_PAYOUT_CLEANING (content) {
      const toolData = {
        content,
        settings: {
          placement: 'top'
        },
        className: 'inline-block'
      };
      return toolData;
    },
    HOST_PAYOUT_FNB (content) {
      const toolData = {
        content,
        settings: {
          placement: 'top'
        },
        className: 'inline-block'
      };
      return toolData;
    },
    GUEST_PAYOUT (content) {
      const toolData = {
        content,
        settings: {
          placement: 'top',
          positionLeft: -126,
          positionTop: content.props.discounts.length ? -230 : -180
        },
        className: 'inline-block'
      };
      return toolData;
    },
    GUEST_PAYOUT_UPDATE (content) {
      const toolData = {
        content,
        settings: {
          placement: 'bottom'
        },
        className: 'inline-block'
      };
      return toolData;
    },
    GUEST_PAYOUT_CLEANING (content) {
      const toolData = {
        content,
        settings: {
          placement: 'top'
        },
        className: 'inline-block'
      };
      return toolData;
    },
    GUEST_PAYOUT_FNB (content) {
      const toolData = {
        content,
        settings: {
          placement: 'top'
        },
        className: 'inline-block'
      };
      return toolData;
    },
    PROCESSING_FEE: {
      content:
        <div>
          This covers costs that allow Peerspace to complete your transaction and provide support for your booking. <a href={`//support.peerspace.com/hc/${zendeskLocale}/articles/214284346`} target="_blank">Learn More</a>
        </div>,
      settings: {
        placement: 'right'
      },
      className: 'inline-block'
    },
    CLEANING_FEE: {
      content:
        <div>
          This covers the cleaning of the space after your booking. <a href={`//support.peerspace.com/hc/${zendeskLocale}/articles/115002477043`} target="_blank">Learn More</a>
        </div>,
      settings: {
        placement: 'right'
      },
      className: 'inline-block'
    },
    DIFFERENCE_TOTAL ({ content }) {
      return {
        content,
        settings: {
          placement: 'bottom'
        },
        className: 'inline-block'
      };
    },
    CURRENT_TOTAL ({ content }) {
      return {
        content,
        settings: {
          placement: 'bottom'
        },
        className: 'inline-block'
      };
    },
    VARIABLE_PRICING (listing, rateDetails) {
      return {
        content: (
          <VariablePricingTooltip listing={listing} rateDetails={rateDetails} />
        ),
        settings: {
          placement: 'bottom'
        },
        className: 'inline-block margin-sm-left'
      };
    },
    DAY_RATE_DISCOUNT ({ currencyType = 'USD', price, discount, interestedSection = false, interestedFn }) {
      const { value } = discount;

      const hoursTotal = price * 8;
      const discountAmount = hoursTotal * (value / 100);
      const finalTotal = hoursTotal - discountAmount;

      const toolData = {
        content:
        <IntlProviderHOC>
          <div className="summary-block">
            <div className="summary-description">
              This space offers a discount for bookings 8-hours or more.
            </div>
            <div className="summary-content">
              <FormattedNumber value={price} currency={currencyType} style={'currency'} /> x 8 hours
              <span className="pull-right">
                <FormattedNumber value={hoursTotal} currency={currencyType} style={'currency'} />
              </span>
            </div>
            <div className="summary-content">
              {value}% Discount
              <span className="pull-right">
                <FormattedNumber value={discountAmount} currency={currencyType} style={'currency'} />
              </span>
            </div>
            <hr className="summary-line" />
            <h5 className="summary-total">
              Total <span className="pull-right"><FormattedNumber value={finalTotal} currency={currencyType} style={'currency'} /></span>
            </h5>
            <br />
            <div className="text-small summary-content">
              * Cleaning and processing fee not included in this total.
            </div>

            { interestedSection ?

              <div className="summary-content text-center day-rate-interested">
                <br />
                Interested? <a onClick={interestedFn}>Start Booking</a>
              </div>
              : null
            }
          </div>
        </IntlProviderHOC>,
        settings: {
          placement: 'top'
        },
        autoScroll: true,
        className: 'inline-block'
      };

      return toolData;
    },
    SAVE_TO_PROJECTS_VIA_SEARCH_PAGE (boundToggleFn) {
      const toolData = {
        content: (
          <div>
            <div onClick={boundToggleFn} style={{ position: 'absolute', right: '6px', top: '0' }}>
              <i className="ico icon-no ignore-clickoutside" style={{ fontSize: '8px', cursor: 'pointer', paddingRight: '4px' }} onClick={boundToggleFn} onKeyPress={boundToggleFn}></i>
            </div>
            <i className="ico icon-full-heart-plus" style={{ fontSize: '22px' }} />
            <span>  <b>Save</b> a space you love.</span>
          </div>
        ),
        settings: {
          placement: 'bottom',
          arrowOffsetLeft: 188
        },
        autoScroll: true,
        popoverClassName: 'blue-tooltip'
      };
      const toolTip = (
        <div
          className="first-time-save"
          style={ { width: '238px', position: 'relative', float: 'right', right: '-7px', top: '-39px' } }
        >
          <TooltipPopover
            toggle={boundToggleFn}
            {...toolData}
          />
        </div>
      );
      return toolTip;
    },
    POWER_HOST ({ mobile }) {
      const toolData = {
        content: (
          <div className="power-host-tooltip">
            Power Hosts meet the highest standard for hosting on Peerspace. <a href="http://try.peerspace.com/power-host/" target="_blank">Learn More</a>
          </div>
        ),
        settings: {
          placement: mobile ? 'bottom' : 'left'
        },
        container: ' '
      };
      return toolData;
    },
    TAX_INVOICES: (isHost) => ({
      content:
        <div>
          <FormattedMessage
            id='inbox.tooltip_data.multiple_tax_invoices'
            defaultMessage='If you updated your tax details, you may receive multiple invoices for a single month. {learnMore}'
            values={{
              learnMore: (
                <a href={isHost ? hostVatSupportLink : guestVatSupportLink} target="_blank">
                  <FormattedMessage
                    id='inbox.tooltip_data.learn_more_service'
                    defaultMessage='Learn More'
                  />
                </a>
              )
            }}
          />
        </div>,
      settings: {
        placement: 'top'
      },
      className: 'inline-block'
    }),
    ADD_ONS_TAX: {
      content:
        <div className="text-gray-dark">
          <FormattedMessage
            id="inbox.tooltip_data.add_ons_tax"
            defaultMessage="You should not specify any taxes if Peerspace is responsible for collecting & remitting taxes in your state. {clickHere} to determine whether this applies to you."
            values={{
              clickHere: (
                <a className="underline text-gray-darker" href={`https://support.peerspace.com/hc/${zendeskLocale}/articles/26817436927508-Reporting-sales-tax-on-your-bookings`} target="_blank">
                  <FormattedMessage
                    id="inbox.tooltip_data.add_ons_tax_link"
                    defaultMessage="Click here"
                  />
                </a>
              )
            }}
          />
        </div>,
      settings: {
        placement: 'right'
      },
      className: 'inline-block'
    }
  };
};

export class Tooltip extends Component {
  constructor (props) {
    super(props);

    const { showOnRender } = props;

    this.state = {
      show: !!showOnRender
    };

    this.toggle = this.toggle.bind(this);
    this.toggleOnly = this.toggleOnly.bind(this);
  }

  toggle (e) {
    if (e) e.preventDefault();

    const { show } = this.state;
    const { tooltip } = this.props;
    if (!show && tooltip.autoScroll) {
      $('html, body').animate({
        scrollTop: $(e.target).offset().top - 500
      }, 300);
    }

    return this.setState({ show: !show });
  }

  // function used to toggle SAVE_TO_PROJECTS_VIA_SEARCH_PAGE
  toggleOnly (e) {
    const { show } = this.state;
    const { customTooltipCB, customTooltipCBParam } = this.props;
    this.setState({ show: !show });
    if (customTooltipCB) {
      return customTooltipCB(customTooltipCBParam);
    }
  }
  render () {
    const { tooltip, customTooltip, children } = this.props;
    const { show } = this.state;

    const genericTooltip = (
      <button onClick={this.toggle} onKeyPress={this.toggle} className="btn link-gray remove-click-styling no-gutter-all">
        <i className="ico ico-md icon-question" />
      </button>
    );

    const tooltipToShow = (
      <div className={tooltip.className || ''}>
        <div id="tooltip-container" className="tooltip-container">
          { show ?
            <TooltipPopover toggle={this.toggle} {...tooltip} >{ children }</TooltipPopover> : ''
          }
        </div>
        <div onClick={this.toggle} onKeyPress={this.toggle}>
          { customTooltip || genericTooltip }
        </div>
      </div>
    );

    const forcedTooltip = customTooltip ? (
      <div className="custom-tooltip">
        { show ? 'function' === typeof customTooltip ? customTooltip(this.toggleOnly) : customTooltip : '' }
      </div>)
      : genericTooltip;

    return (
      <span className={tooltip.container || 'tooltip-outer-container'}>
        { this.props.forceCustom ? forcedTooltip : tooltipToShow }
      </span>
    );
  }
}

Tooltip.propTypes = {
  tooltip: PropTypes.object,
  showOnRender: PropTypes.bool,
  customTooltip: PropTypes.object,
  customTooltipCB: PropTypes.func
};
